import React, { useLayoutEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { ResetPasswordRequest } from '@/types/Employee.type';
import URL from '@/common/routes/Url';
import { useAuth } from '@/common/hooks/useAuth';
import { Grid, Typography } from '@mui/material';
import Copyright from '@/components/common/Copyright';
import HookFormControls, { HookFormControlsProps } from '@/components/common/HookFormControls';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';

const ResetPassword: React.FC = () => {
  const location = useLocation();
  const [selectEmail, setSelectEmail] = useState<{ email: string }>(location.state as { email: string });
  const auth = useAuth();
  const useFormMethods = useForm<ResetPasswordRequest>({
    shouldUnregister: false,
    defaultValues: { email: selectEmail ? selectEmail.email : '' },
  });
  const { handleSubmit, getValues } = useFormMethods;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const inputs: Array<HookFormControlsProps> = [
    {
      name: 'email',
      type: 'text',
      label: 'メールアドレス*',
      helper: 'ログインに使用するメールアドレスを入力してください。',
      rule: {
        required: 'メールアドレスは必須項目です。',
        maxLength: {
          value: 255,
          message: 'メールアドレスは255文字以内で入力してください。',
        },
        pattern: {
          value: new RegExp(
            "^[a-zA-Z0-9\\.\\!\\#\\$\\%\\&\\'\\*\\+\\/\\=\\\\\\?\\^\\_\\`\\{\\|\\}\\~\\-]+\\@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$",
          ),
          message: 'メールアドレスの形式が不正です。',
        },
      },
    },
    {
      name: 'code',
      type: 'text',
      label: '認証コード*',
      helper: 'メールで受信した認証コードを入力してください。',
      rule: {
        required: '認証コードは必須項目です。',
        maxLength: {
          value: 6,
          message: '認証コードは6文字以内で入力してください。',
        },
        pattern: {
          value: new RegExp('^[0-9]+$'),
          message: '認証コードは数字で入力してください。',
        },
      },
    },
    {
      name: 'password',
      type: 'password',
      label: '新パスワード*',
      helper:
        '※パスワードは8～99文字の半角小文字＋大文字＋数字を組み合わせてください。特殊文字を含める場合は^$*.[]{}()?"!@#%&/\\,><\':;|_~`=+-が利用可能です。',
      rule: {
        required: '新パスワードは必須項目です。',
        pattern: {
          value: new RegExp(
            '^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])[a-zA-Z0-9\\^\\$\\*\\.\\[\\]\\{\\}\\(\\)\\?\\"\\!\\@\\#\\%\\&\\/\\\\,\\>\\<\\\'\\:\\;\\|\\_\\~\\`\\=\\+\\-]*$',
          ),
          message: 'パスワードは半角小文字＋大文字＋数字を組み合わせてください。',
        },
        minLength: {
          value: 8,
          message: 'パスワードは8文字以上で入力してください。',
        },
        maxLength: {
          value: 99,
          message: 'パスワードは99文字以下で入力してください。',
        },
      },
    },
    {
      name: 'password_confirmation',
      type: 'password',
      label: '新パスワード（確認）*',
      rule: {
        required: '新パスワード（確認）は必須項目です。',
        validate: (value) => value === getValues('password') || '新パスワードと新パスワード（確認）の入力が一致しません。',
      },
    },
  ];

  useLayoutEffect(() => {
    auth.signOut();
  }, []);

  const onSubmit = async (values: ResetPasswordRequest) => {
    try {
      const result = await auth.forgotPasswordSubmit(values.email, values.code, values.password);
      if (result) {
        enqueueSnackbar('パスワードを変更しました。', { variant: 'info' });
        navigate(`${URL.signin}`);
        return;
      }
      enqueueSnackbar('パスワードの変更に失敗しました。');
    } catch (error: any) {
      console.log(error);

      enqueueSnackbar('パスワードの変更に失敗しました。');
      return;
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ marginTop: 12 }}>
      <Grid item xs={12} md={8} lg={4} sx={{ mx: 4 }}>
        <FormProvider {...useFormMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {inputs.map((input, index) => (
                <Grid item key={index} xs={12}>
                  <HookFormControls key={index} {...input} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <LoadingButton type="submit" fullWidth variant="contained" sx={{ mb: 2 }} loading={auth.isLoading}>
                  更新
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
        <Copyright />
      </Grid>
    </Grid>
  );
};
export default ResetPassword;
